import React, { useState, useEffect, useRef } from "react";

import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import { businessService } from "../../../services/business.service";
import {
  INITIAL_VALUES,
  SETUP_STEPS,
  VALIDATION_SCHEMA,
  SHOP_TYPES,
  PAYMENT_METHODS,
} from "./constant";
import moment from 'moment';

import { Button } from "primereact/button";
import { Steps } from "primereact/steps";
import { Toast } from "primereact/toast";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Dialog } from "primereact/dialog";

import CombinedInformationSection from "./components/CombinedInformationSection";
import ServiceForm from "../../../components/forms/ServiceForm/ServiceForm";
import FoodForm from "../../../components/forms/FoodForm/FoodForm";
import TransportForm from "../../../components/forms/TransportForm/TransportForm";
import TechForm from "../../../components/forms/TechForm/TechForm";
import RetailForm from "../../../components/forms/RetailForm/RetailForm";
import RealEstateForm from "../../../components/forms/RealEstateForm/RealEstateForm";
import ProfessionalForm from "../../../components/forms/ProfessionalForm/ProfessionalForm";
import EducationForm from "../../../components/forms/EducationForm/EducationForm";
import HealthWellnessForm from "../../../components/forms/HealthWellnessForm/HealthWellnessForm";
import EntertainmentForm from "../../../components/forms/EntertainmentLeisureForm/EntertainmentLeisureForm";
import BusinessHoursForm from "../../../components/forms/BusinessHoursForm/BusinessHoursForm";
import PaymentMethodForm from "../../../components/forms/PaymentMethodForm/PaymentMethodForm";

import { useAppStore } from "../../../store";
import { selectAuthSession } from "../../../store/common/auth/slice";
import {
  selectUserRecordById,
  selectUserRecordFlagsById,
} from "../../../store/common/userRecord/slice";

import "./styles.css";

const FirstTimeSetup = () => {
  const session = useAppStore(selectAuthSession);
  const uid = session?.uid;
  const userData = useAppStore(selectUserRecordById(uid));
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameCheckStatus, setNameCheckStatus] = useState({
    loading: false,
    available: null,
    message: "",
  });
  const checkTimeout = useRef(null);
  const toast = useRef(null);
  const stateRef = useRef({});
  const navigate = useNavigate();


  useEffect(() => {
    if (!uid) {
      return;
    }
    useAppStore.getState().common.userRecord.getById({
      id: uid,
      subscribe: true,
      prefetch: false,
    });
  }, [uid]);

  const getInitialValues = () => {
    const initialValues = { ...INITIAL_VALUES };

    if (userData) {
      // Deep merge user data into initial values
      Object.keys(userData).forEach((key) => {
        if (INITIAL_VALUES[key] !== undefined) {
          // Handle nested objects
          if (typeof userData[key] === 'object' && userData[key] !== null && !Array.isArray(userData[key])) {
            initialValues[key] = { ...INITIAL_VALUES[key], ...userData[key] };
          } else {
            initialValues[key] = userData[key];
          }
        }
      });

      // Handle shopType specifically
      if (userData.shopType) {
        const rawShopTypes = Array.isArray(userData.shopType)
          ? userData.shopType
          : [userData.shopType];
        initialValues.shopType = rawShopTypes.filter((st) => st);
      }
    }

    return initialValues;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!uid) {
      return;
    }

    try {
      // Save first page (profile) into 'users' collection

      await useAppStore.getState().features.firstTimeSetup.updateUserProfile({
        uid,
        values: { ...values, setupCompleted: true, updatedAt: moment().unix() },
      });

      // Prepare business at a glance data with all required fields
      const businessAtAGlanceData = {
        shopName: values.shopName || "",
        contactEmail: values.contactEmail || "",
        markerPosition: values.markerPosition || null,
        storeAddress: values.storeAddress || "",
        directions: values.directions || "",
        storeImage: values.storeImage || null,
        updatedAt: moment().unix(),
      };

        

      // Save business at a glance data
      await useAppStore
        .getState()
        .features.firstTimeSetup.saveBusinessAtAGlance({
          uid,
          values: businessAtAGlanceData,
        });

      // Save second page (business configuration) into 'businesses'
      await useAppStore.getState().features.firstTimeSetup.saveBusinessConfig({
        uid,
        values,
      });

      // Redirect to dashboard or next step
      navigate("/dashboard");
    } catch (error) {
      console.error("Error during first-time setup:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save settings. Please try again.",
        life: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndSkip = async (values, { setSubmitting }) => {
    if (!uid) {
      return;
    }
    try {
      const valuesWithEmptyShopType = {
        ...values,
        shopType: [],
      };
      await useAppStore.getState().features.firstTimeSetup.updateUserProfile({
        uid,
        values: valuesWithEmptyShopType,
      });

      // Also persist an initial (possibly empty) business configuration document
      await useAppStore.getState().features.firstTimeSetup.saveBusinessConfig({
        uid,
        values: valuesWithEmptyShopType,
      });
    } catch (error) {
      console.error("Error during save and skip:", error);
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  const getSteps = (shopType) => {
    const baseSteps = SETUP_STEPS.base;
    const shopTypes = Array.isArray(shopType)
      ? shopType
      : shopType
      ? [shopType]
      : [];

    const categories = shopTypes.reduce((acc, type) => {
      const category = Object.values(SHOP_TYPES).find(
        (category) =>
          category.items && category.items.some((item) => item.value === type)
      );

      if (category && !acc.includes(category.label)) {
        acc.push(category.label);
      }
      return acc;
    }, []);

    const groupedSteps = categories.map((category) => ({
      label: category,
      icon: "pi pi-file-arrow-up",
      description: `Configure your ${category
        .toLowerCase()
        .replace(" & ", " and ")} details`,
      category: category,
      shopTypes: shopTypes.filter((type) => {
        const categoryObj = Object.values(SHOP_TYPES).find(
          (cat) => cat.items && cat.items.some((item) => item.value === type)
        );
        return categoryObj?.label === category;
      }),
    }));

    return [...baseSteps, ...groupedSteps];
  };

  const renderStepContent = (
    step,
    values,
    errors,
    touched,
    handleChange,
    setFieldValue
  ) => {
    const steps = getSteps(values.shopType);
    const currentStep = steps[step];

    switch (step) {
      case 0:
        return (
          <CombinedInformationSection
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            userData={userData}
            stateRef={stateRef}
            nameCheckStatus={nameCheckStatus}
            onNameChange={handleNameChange}
          />
        );
      default:
        if (currentStep.category) {
          const categoryForms = {
            Service: ServiceForm,
            "Food & Beverage": FoodForm,
            "Transportation & Automotive": TransportForm,
            "Technology & Online": TechForm,
            Retail: RetailForm,
            "Real Estate & Housing": RealEstateForm,
            "Professional Services": ProfessionalForm,
            "Education & Training": EducationForm,
            "Health & Wellness": HealthWellnessForm,
            "Entertainment & Leisure": EntertainmentForm,
          };

          const FormComponent = categoryForms[currentStep.category];
          if (FormComponent && currentStep.shopTypes) {
            return (
              <div>
                <div className="shared-settings">
                  {values.sharedSettings && (
                    <div className="shared-forms">
                      <div className="shared-form-section">
                        <BusinessHoursForm
                          shopType={currentStep.shopTypes[0]}
                          formState={values}
                          setFormState={(field, value) =>
                            setFieldValue(field, value)
                          }
                        />
                      </div>

                      <div className="shared-form-section">
                        <PaymentMethodForm
                          shopType={currentStep.shopTypes[0]}
                          formState={values}
                          setFormState={(field, value) =>
                            setFieldValue(field, value)
                          }
                          paymentMethods={PAYMENT_METHODS}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Accordion multiple>
                  {currentStep.shopTypes.map((shopType, index) => (
                    <AccordionTab
                      key={shopType}
                      header={
                        SHOP_TYPES.find(
                          (cat) =>
                            cat.items &&
                            cat.items.some((item) => item.value === shopType)
                        )?.items.find((item) => item.value === shopType)
                          ?.label || shopType
                      }
                    >
                      <FormComponent
                        shopType={shopType}
                        formState={values}
                        setFormState={(field, value) =>
                          setFieldValue(field, value)
                        }
                      />

                      {!values.sharedSettings && (
                        <div className="individual-settings">
                          <div className="individual-form-section">
                            <BusinessHoursForm
                              shopType={shopType}
                              formState={values}
                              setFormState={(field, value) =>
                                setFieldValue(field, value)
                              }
                            />
                          </div>

                          <div className="individual-form-section">
                            <PaymentMethodForm
                              shopType={shopType}
                              formState={values}
                              setFormState={(field, value) =>
                                setFieldValue(field, value)
                              }
                              paymentMethods={PAYMENT_METHODS}
                            />
                          </div>
                        </div>
                      )}
                    </AccordionTab>
                  ))}
                </Accordion>
              </div>
            );
          }
        }
        return null;
    }
  };

  const handleNameChange = async (e, setFieldValue, setFieldError) => {
    const name = e.target.value;

    if (checkTimeout.current) {
      clearTimeout(checkTimeout.current);
    }

    checkTimeout.current = setTimeout(async () => {
      const name = e.target.value;

      if (!name) {
        setNameCheckStatus({
          loading: false,
          available: null,
          message: "",
        });
        return;
      }

      setNameCheckStatus((prev) => ({ ...prev, loading: true }));

      try {
        const result = await businessService.checkBusinessName(name);
        const status = {
          loading: false,
          available: result.available,
          message: result.message,
        };

        setNameCheckStatus(status);

        if (status.available === false) {
          setFieldError("shopName", status.message);
        } else {
          setFieldError("shopName", undefined);
        }
      } catch (error) {
        console.error("Error checking business name:", error);
        const status = {
          loading: false,
          available: null,
          message: "Error checking name availability",
        };
        setNameCheckStatus(status);
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (checkTimeout.current) {
        clearTimeout(checkTimeout.current);
      }
    };
  }, []);

  return (
    <div className="first-time-setup">
      <Toast ref={toast} />
      <div className="flex justify-content-flex-start">
        <Button
          label="Sign Out"
          icon="pi pi-arrow-left"
          className="p-button-text p-button-danger"
          onClick={() => {
            const signOutFn = useAppStore.getState().common?.auth?.signOut;
            Promise.resolve(signOutFn ? signOutFn() : undefined).finally(() => {
              window.dispatchEvent(new CustomEvent("logout"));
              navigate("/");
            });
          }}
        />
      </div>
      <div className="w-full align-items-center  ">
        <div className="flex display-flex flex-column align-items-center justify-content-center">
          <h1 className="text-3xl font-bold mb-2">
            {userData?.shopName ? 'Just confirming your infos again.' : 'Please complete your business setup to get started.'}
          </h1>
          <p className="text-gray-600">
            {userData?.shopName 
              ? 'Please review and confirm your business details.' 
              : 'Fill in the required information to get your business profile ready.'}
          </p>
        </div>
      </div>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          isValid,
          dirty,
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          setSubmitting,
          setFieldTouched,
        }) => {
          const steps = getSteps(values.shopType);

          return (
            <Form className="setup-form">
              <Steps
                model={steps.map((step) => ({
                  ...step,
                  icon: undefined,
                }))}
                activeIndex={activeStep}
                readOnly={false}
                className="setup-steps"
              />

              {renderStepContent(
                activeStep,
                values,
                errors,
                touched,
                handleChange,
                setFieldValue
              )}

              <div className="form-navigation">
                {steps.length === 1 ? (
                  <Button
                    type="submit"
                    label={userData?.shopName ? "Confirm" : "Complete Setup"}
                    icon="pi pi-check"
                    loading={isSubmitting}
                    className="p-button-primary"
                  />
                ) : (
                  <>
                    <Button
                      type="button"
                      label={
                        activeStep < steps.length - 1
                          ? "Next"
                          : "Complete Setup"
                      }
                      icon={
                        activeStep < steps.length - 1
                          ? "pi pi-arrow-right"
                          : "pi pi-check"
                      }
                      onClick={
                        activeStep < steps.length - 1
                          ? async () => {
                              if (activeStep === 0) {
                                const requiredFields = [
                                  "shopName",
                                  "contactEmail",
                                  "contactPhone",
                                  "storeAddress",
                                  "directions",
                                ];
                                requiredFields.forEach((field) => {
                                  setFieldValue(field, values[field]);
                                  setFieldTouched(field, true);
                                });

                                try {
                                  await VALIDATION_SCHEMA.validate(values, {
                                    abortEarly: false,
                                  });
                                } catch (error) {
                                  const firstError =
                                    error.inner[0]?.message ||
                                    "Please fill in all required fields";
                                  toast.current?.show({
                                    severity: "error",
                                    summary: "Validation Error",
                                    detail: firstError,
                                    life: 3000,
                                  });

                                  error.inner.forEach((err) => {
                                    const field = err.path;
                                    if (field) {
                                      setFieldTouched(field, true);
                                    }
                                  });
                                  return;
                                }

                                if (
                                  !values.shopType ||
                                  (Array.isArray(values.shopType) &&
                                    values.shopType.length === 0)
                                ) {
                                  toast.current?.show({
                                    severity: "error",
                                    summary: "Validation Error",
                                    detail:
                                      "Please select at least one shop type",
                                    life: 3000,
                                  });
                                  return;
                                }
                              }
                              setActiveStep(activeStep + 1);
                            }
                          : async () => {
                              await handleSubmit(values, { setSubmitting });
                            }
                      }
                      loading={isSubmitting}
                      className="p-button-primary"
                    />
                    {activeStep === 0 && (
                      <Button
                        type="button"
                        label="Save and Skip"
                        icon="pi pi-fast-forward"
                        onClick={async () => {
                          const valuesWithEmptyShopType = {
                            ...values,
                            shopType: [],
                          };

                          const formik = {
                            values: valuesWithEmptyShopType,
                            setSubmitting,
                          };
                          await handleSubmit(valuesWithEmptyShopType, formik);
                        }}
                        className="p-button-text"
                      />
                    )}
                  </>
                )}
                {activeStep > 0 && (
                  <Button
                    type="button"
                    label="Back"
                    icon="pi pi-arrow-left"
                    onClick={() => setActiveStep(activeStep - 1)}
                    className="p-button-text"
                  />
                )}
              </div>
            </Form>
          );
        }}
      </Formik>

      
    </div>
  );
};

export default FirstTimeSetup;
